/**
 * Statistici — ce se întâmplă, de fapt, cu meniul în restaurant.
 *
 * Pagina este construită pe ideea că un proprietar de restaurant nu are timp să „exploreze" un
 * dashboard: fiecare bloc trebuie să răspundă la o întrebare pe care și-o pune oricum.
 *   • Câți oameni s-au uitat la meniu? (vizualizări, vizitatori unici, durata)
 *   • Când vin? (ore — ca să știe pentru ce ore pune personal)
 *   • Ce privesc? (produse top, căutări)
 *   • Cu ce? (telefon/tabletă, limbă, țară)
 *   • Ce coduri și ecrane funcționează? (scanări pe cod, uptime ecrane)
 *   • Ce ar merita schimbat? (semnalele „insights", formulate ca acțiuni)
 *
 * Confidențialitate: nu se afișează niciodată date personale. Vizitatorii unici sunt un număr
 * agregat, calculat pe server cu o cheie zilnică, fără IP și fără amprentă de dispozitiv (GDPR, #31).
 */
import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Icon,
  InlineAlert,
  KpiCard,
  PageHeader,
  Select,
  Skeleton,
  StatusDot,
  cx,
  useToast,
} from '../components/ui.js';
import { BreakdownBars, HourBars, ShareBar, TrendChart } from '../components/charts.js';
import { api } from '../lib/api.js';
import { formatDateTime, formatNumber, useResource } from '../lib/hooks.js';
import { errorMessage } from '../lib/session.js';

interface Overview {
  range: { from: string; to: string };
  previous: { from: string; to: string };
  kpis: {
    views: number;
    uniqueVisitors: number;
    scans: number;
    avgSeconds: number;
    displayUptimeSeconds: number;
    displaysOnline: number;
    displaysTotal: number;
    promosActive: number;
    qrCodes: number;
    productsAvailable: number;
    productsTotal: number;
  };
  delta: Record<string, number>;
  series: Array<{ day: string; views: number; scans: number; uniqueVisitors: number; avgSeconds: number }>;
}

interface Breakdown {
  devices: Array<{ key: string; count: number }>;
  languages: Array<{ key: string; count: number }>;
  countries: Array<{ key: string; count: number }>;
  hours: number[];
  searches: Array<{ term: string; count: number }>;
  qrByCode: Array<{ label: string; code: string; scans: number }>;
}

interface ProductRow {
  id: string;
  name: string;
  category: string | null;
  priceCents: number;
  isAvailable: boolean;
  views: number;
  qrViews: number;
  displayImpressions: number;
  promoViews: number;
}

interface LocationRow {
  id: string;
  name: string;
  city: string | null;
  scans: number;
  views: number;
  displays: number;
  displaysOnline: number;
  uptimeSeconds: number;
}

interface DisplayRow {
  id: string;
  name: string;
  zone: string | null;
  status: string;
  uptimeSeconds: number;
  heartbeats: number;
  reconnects: number;
  offlineSeconds: number;
  contentSwitches: number;
  avgLatencyMs: number;
  series: Array<{ day: string; uptime: number; heartbeats: number }>;
}

interface Insight {
  kind: 'opportunity' | 'warning' | 'positive' | 'info';
  title: string;
  detail: string;
  metric?: string;
}

const RANGES = [
  { value: '7', label: 'Ultimele 7 zile' },
  { value: '30', label: 'Ultimele 30 de zile' },
  { value: '90', label: 'Ultimele 90 de zile' },
];

const DEVICE_LABELS: Record<string, string> = {
  mobile: 'Telefon',
  tablet: 'Tabletă',
  desktop: 'Calculator',
  other: 'Altele',
  unknown: 'Necunoscut',
};

const COUNTRY_LABELS: Record<string, string> = {
  RO: 'România',
  DE: 'Germania',
  FR: 'Franța',
  IT: 'Italia',
  GB: 'Marea Britanie',
  US: 'Statele Unite',
  ES: 'Spania',
  AT: 'Austria',
  HU: 'Ungaria',
  MD: 'Republica Moldova',
};

const LANGUAGE_LABELS: Record<string, string> = {
  ro: 'Română',
  en: 'Engleză',
  de: 'Germană',
  fr: 'Franceză',
  it: 'Italiană',
  es: 'Spaniolă',
};

const INSIGHT_STYLES: Record<Insight['kind'], { tone: 'success' | 'warning' | 'info' | 'brand'; icon: string; label: string }> = {
  opportunity: { tone: 'brand', icon: '💡', label: 'Oportunitate' },
  warning: { tone: 'warning', icon: '⚠️', label: 'De verificat' },
  positive: { tone: 'success', icon: '📈', label: 'Funcționează' },
  info: { tone: 'info', icon: 'ℹ️', label: 'Observație' },
};

function formatDuration(seconds: number): string {
  if (!seconds) return '0s';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  if (hours < 48) return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
  return `${Math.round(hours / 24)} zile`;
}

function trend(value: number | undefined): { value: string; positive: boolean } | undefined {
  if (value === undefined || Number.isNaN(value)) return undefined;
  const sign = value > 0 ? '+' : '';
  return { value: `${sign}${value}%`, positive: value >= 0 };
}

export function AnalyticsPage() {
  const toast = useToast();
  const [days, setDays] = useState('30');

  const overview = useResource<Overview>(() => api.get<Overview>(`/analytics/overview?days=${days}`), [days]);
  const breakdown = useResource<Breakdown>(() => api.get<Breakdown>(`/analytics/breakdown?days=${days}`), [days]);
  const products = useResource<{ products: ProductRow[] }>(
    () => api.get<{ products: ProductRow[] }>(`/analytics/products?days=${days}&limit=10`),
    [days],
  );
  const locations = useResource<{ locations: LocationRow[] }>(
    () => api.get<{ locations: LocationRow[] }>(`/analytics/locations?days=${days}`),
    [days],
  );
  const displays = useResource<{ displays: DisplayRow[] }>(
    () => api.get<{ displays: DisplayRow[] }>(`/analytics/displays?days=${days}`),
    [days],
  );
  const insights = useResource<{ insights: Insight[] }>(() => api.get<{ insights: Insight[] }>(`/analytics/insights?days=${days}`), [days]);

  const [exporting, setExporting] = useState(false);
  const kpis = overview.data?.kpis;
  const series = overview.data?.series ?? [];

  const deviceRows = useMemo(
    () => (breakdown.data?.devices ?? []).map((row) => ({ key: row.key, label: DEVICE_LABELS[row.key] ?? row.key, value: row.count })),
    [breakdown.data],
  );
  const languageRows = useMemo(
    () => (breakdown.data?.languages ?? []).map((row) => ({ key: row.key, label: LANGUAGE_LABELS[row.key] ?? row.key, value: row.count })),
    [breakdown.data],
  );
  const countryRows = useMemo(
    () => (breakdown.data?.countries ?? []).map((row) => ({ key: row.key, label: COUNTRY_LABELS[row.key] ?? row.key, value: row.count })),
    [breakdown.data],
  );

  async function exportCsv() {
    setExporting(true);
    try {
      // Exportul se descarcă prin API (cu sesiunea curentă), nu printr-un link public: datele
      // rămân izolate pe cont, chiar și la descărcare.
      await api.download(`/analytics/export.csv?dataset=daily&days=${days}`, `menuvia-statistici-${days}z.csv`);
      toast.success('Export pregătit', 'Fișierul CSV a fost descărcat.');
    } catch (error) {
      toast.error('Exportul nu a reușit', errorMessage(error));
    } finally {
      setExporting(false);
    }
  }

  const loading = overview.loading;
  const failed = overview.error;

  return (
    <div>
      <PageHeader
        title="Statistici"
        subtitle={`Cum este folosit meniul tău, ${overview.data?.range.from ?? ''} → ${overview.data?.range.to ?? ''}`}
        actions={
          <>
            <Select value={days} onChange={(event) => setDays(event.target.value)} aria-label="Perioada analizată" className="w-44">
              {RANGES.map((range) => (
                <option key={range.value} value={range.value}>
                  {range.label}
                </option>
              ))}
            </Select>
            <Button variant="secondary" icon={<Icon name="download" />} loading={exporting} onClick={exportCsv}>
              Export CSV
            </Button>
          </>
        }
      />

      {failed ? (
        <InlineAlert tone="danger" title="Nu am putut încărca statisticile">
          {failed}{' '}
          <button type="button" className="underline" onClick={() => void overview.reload()}>
            Reîncearcă
          </button>
        </InlineAlert>
      ) : null}

      {/* ─────────────── indicatori principali ─────────────── */}
      <section aria-label="Indicatori" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {loading || !kpis
          ? Array.from({ length: 4 }).map((_, index) => (
              <Card key={index} className="flex flex-col gap-3">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 w-20" />
                <Skeleton className="h-3 w-32" />
              </Card>
            ))
          : (
              <>
                <KpiCard
                  label="Vizualizări meniu"
                  value={formatNumber(kpis.views)}
                  trend={trend(overview.data?.delta.views)}
                  hint={`${formatNumber(kpis.uniqueVisitors)} vizitatori unici`}
                />
                <KpiCard
                  label="Scanări QR"
                  value={formatNumber(kpis.scans)}
                  trend={trend(overview.data?.delta.scans)}
                  hint={`${kpis.qrCodes} coduri active`}
                />
                <KpiCard
                  label="Timp mediu pe meniu"
                  value={formatDuration(kpis.avgSeconds)}
                  trend={trend(overview.data?.delta.avgSeconds)}
                  hint="cât stă un client în meniu"
                />
                <KpiCard
                  label="Ecrane online"
                  value={`${kpis.displaysOnline}/${kpis.displaysTotal}`}
                  hint={
                    kpis.displaysTotal
                      ? `${formatDuration(kpis.displayUptimeSeconds)} de funcționare cumulată`
                      : 'niciun ecran conectat încă'
                  }
                />
              </>
            )}
      </section>

      {/* ─────────────── evoluția zilnică ─────────────── */}
      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader
            title="Evoluția zilnică"
            subtitle="Vizualizări de meniu și scanări QR — canale separate, fără dublă numărare"
            action={
              <div className="flex items-center gap-3 text-caption text-ink-600">
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-[var(--color-brand-600)]" /> vizualizări
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-[var(--color-accent-500)]" /> scanări
                </span>
              </div>
            }
          />
          {loading ? (
            <Skeleton className="h-56" />
          ) : series.length ? (
            <TrendChart
              points={series}
              series={[
                { key: 'views', label: 'Vizualizări', color: 'var(--color-brand-600)' },
                { key: 'scans', label: 'Scanări QR', color: 'var(--color-accent-500)', area: false },
              ]}
              formatValue={(value) => formatNumber(value)}
            />
          ) : (
            <EmptyState title="Încă nu sunt date în perioada selectată" description="Statisticile apar după primele scanări și vizualizări de meniu." />
          )}
        </Card>

        <Card>
          <CardHeader title="Când îți deschid clienții meniul" subtitle="Distribuția pe ore a zilei" />
          {breakdown.loading ? (
            <Skeleton className="h-40" />
          ) : (
            <HourBars hours={breakdown.data?.hours ?? Array(24).fill(0)} />
          )}
        </Card>
      </div>

      {/* ─────────────── produse, dispozitive, limbi ─────────────── */}
      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader
            title="Cele mai privite produse"
            subtitle="Câte ori a fost deschis un produs, pe fiecare canal"
            action={<Badge tone="neutral">top 10</Badge>}
          />
          {products.loading ? (
            <div className="flex flex-col gap-3">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-10" />
              ))}
            </div>
          ) : products.data?.products.length ? (
            <div className="-mx-5 overflow-x-auto px-5">
              <table className="w-full min-w-[560px] border-collapse text-body-s">
                <thead>
                  <tr className="text-left text-caption uppercase tracking-wide text-ink-400">
                    <th className="pb-2 font-medium">Produs</th>
                    <th className="pb-2 text-right font-medium">Meniu QR</th>
                    <th className="pb-2 text-right font-medium">Ecrane</th>
                    <th className="pb-2 text-right font-medium">Promoții</th>
                    <th className="pb-2 text-right font-medium">Total</th>
                    <th className="pb-2 pl-3 font-medium">Pondere</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {products.data.products.map((product) => {
                    const total = product.views + product.qrViews + product.displayImpressions + product.promoViews;
                    const maximum = Math.max(
                      ...products.data!.products.map((row) => row.views + row.qrViews + row.displayImpressions + row.promoViews),
                      1,
                    );
                    return (
                      <tr key={product.id} className="text-ink-800">
                        <td className="py-2.5 pr-3">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-ink-950">{product.name}</span>
                            {product.isAvailable ? null : <Badge tone="warning">indisponibil</Badge>}
                          </div>
                          <span className="text-caption text-ink-400">
                            {product.category ?? 'fără categorie'} · {(product.priceCents / 100).toFixed(2)} lei
                          </span>
                        </td>
                        <td className="nums py-2.5 text-right">{formatNumber(product.qrViews)}</td>
                        <td className="nums py-2.5 text-right">{formatNumber(product.displayImpressions)}</td>
                        <td className="nums py-2.5 text-right">{formatNumber(product.promoViews)}</td>
                        <td className="nums py-2.5 text-right font-medium text-ink-950">{formatNumber(total)}</td>
                        <td className="w-24 py-2.5 pl-3">
                          <ShareBar value={total} maximum={maximum} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState title="Niciun produs vizualizat încă" description="De îndată ce clienții deschid meniul, aici apare clasamentul real." />
          )}
        </Card>

        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader title="Dispozitive" subtitle="Cu ce intră clienții în meniu" />
            {breakdown.loading ? <Skeleton className="h-32" /> : <BreakdownBars rows={deviceRows} emptyLabel="Fără date încă." />}
          </Card>
          <Card>
            <CardHeader title="Limbi" subtitle="Ce limbă aleg clienții" />
            {breakdown.loading ? <Skeleton className="h-32" /> : <BreakdownBars rows={languageRows} emptyLabel="Fără date încă." />}
          </Card>
          <Card>
            <CardHeader title="Țări" subtitle="De unde vin vizitatorii (fără date personale)" />
            {breakdown.loading ? <Skeleton className="h-32" /> : <BreakdownBars rows={countryRows} emptyLabel="Fără date încă." />}
          </Card>
        </div>
      </div>

      {/* ─────────────── semnale: ce merită schimbat ─────────────── */}
      <Card className="mt-6">
        <CardHeader
          title="Ce merită făcut în continuare"
          subtitle="Semnale generate din datele tale — nu sfaturi generice"
          action={<Badge tone="brand">{insights.data?.insights.length ?? 0} semnale</Badge>}
        />
        {insights.loading ? (
          <div className="grid gap-3 md:grid-cols-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="h-20" />
            ))}
          </div>
        ) : insights.data?.insights.length ? (
          <div className="grid gap-3 md:grid-cols-2">
            {insights.data.insights.map((insight, index) => {
              const style = INSIGHT_STYLES[insight.kind] ?? INSIGHT_STYLES.info!;
              return (
                <div
                  key={`${insight.title}-${index}`}
                  className={cx(
                    'flex gap-3 rounded-[var(--radius-md)] border border-ink-100 bg-surface-2 p-3.5',
                    insight.kind === 'warning' && 'border-warning/30 bg-warning-bg/40',
                    insight.kind === 'positive' && 'border-success/30 bg-success-bg/40',
                  )}
                >
                  <span aria-hidden className="text-lg leading-6">
                    {style.icon}
                  </span>
                  <div className="flex flex-col gap-0.5">
                    <div className="flex flex-wrap items-center gap-2">
                      {insight.metric ? (
                        <span className="nums flex h-6 min-w-6 items-center justify-center rounded-full bg-surface-3 px-1.5 text-caption font-medium text-ink-800">
                          {insight.metric}
                        </span>
                      ) : null}
                      <span className="text-body-s font-medium text-ink-950">{insight.title}</span>
                      <Badge tone={style.tone}>{style.label}</Badge>
                    </div>
                    <p className="text-caption text-ink-600">{insight.detail}</p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyState
            title="Totul arată bine"
            description="Nu am găsit nimic de corectat în perioada analizată. Revenim cu semnale noi pe măsură ce se strâng date."
          />
        )}
      </Card>

      {/* ─────────────── locații și ecrane ─────────────── */}
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title="Locații" subtitle="Comparație între restaurantele din cont" />
          {locations.loading ? (
            <Skeleton className="h-32" />
          ) : locations.data?.locations.length ? (
            <ul className="flex flex-col divide-y divide-ink-100">
              {locations.data.locations.map((location) => (
                <li key={location.id} className="flex items-center justify-between gap-4 py-3">
                  <div className="flex flex-col">
                    <span className="text-body-s font-medium text-ink-950">{location.name}</span>
                    <span className="text-caption text-ink-400">
                      {location.city ?? '—'} · {location.displays} ecrane ({location.displaysOnline} online)
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-right">
                    <div className="flex flex-col">
                      <span className="nums text-body-s font-medium text-ink-950">{formatNumber(location.scans)}</span>
                      <span className="text-caption text-ink-400">scanări</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="nums text-body-s font-medium text-ink-950">{formatDuration(location.uptimeSeconds)}</span>
                      <span className="text-caption text-ink-400">uptime</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title="Nicio locație configurată" description="Adaugă o locație ca să vezi statistici separate pe fiecare punct de lucru." />
          )}
        </Card>

        <Card>
          <CardHeader title="Ecrane digitale" subtitle="Funcționare, reconectări și schimbări de conținut" />
          {displays.loading ? (
            <Skeleton className="h-32" />
          ) : displays.data?.displays.length ? (
            <ul className="flex flex-col gap-4">
              {displays.data.displays.map((display) => (
                <li key={display.id} className="flex flex-col gap-2 rounded-[var(--radius-md)] border border-ink-100 p-3.5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="flex items-center gap-2 text-body-s font-medium text-ink-950">
                      <StatusDot tone={display.status === 'online' ? 'success' : display.status === 'offline' ? 'danger' : 'neutral'} />
                      {display.name}
                      <span className="text-caption font-normal text-ink-400">{display.zone ?? 'fără zonă'}</span>
                    </span>
                    <Badge tone={display.status === 'online' ? 'success' : 'neutral'}>{display.status}</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-caption text-ink-600 sm:grid-cols-4">
                    <span>
                      uptime <span className="nums font-medium text-ink-950">{formatDuration(display.uptimeSeconds)}</span>
                    </span>
                    <span>
                      reconectări <span className="nums font-medium text-ink-950">{display.reconnects}</span>
                    </span>
                    <span>
                      offline <span className="nums font-medium text-ink-950">{formatDuration(display.offlineSeconds)}</span>
                    </span>
                    <span>
                      latență <span className="nums font-medium text-ink-950">{display.avgLatencyMs} ms</span>
                    </span>
                  </div>
                  {display.offlineSeconds > 3600 ? (
                    <p className="text-caption text-warning">
                      Ecranul a fost offline {formatDuration(display.offlineSeconds)} în perioada analizată — verifică rețeaua din
                      zona {display.zone ?? 'respectivă'}.
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              title="Niciun ecran conectat"
              description={'Conectează un ecran din secțiunea „Ecrane” și aici vei vedea cât timp a funcționat fără întreruperi.'}
            />
          )}
        </Card>
      </div>

      {/* ─────────────── scanări pe cod + căutări ─────────────── */}
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title="Scanări pe cod QR" subtitle="Ce coduri sunt expuse și care chiar funcționează" />
          {breakdown.loading ? (
            <Skeleton className="h-32" />
          ) : breakdown.data?.qrByCode.length ? (
            <ul className="flex flex-col divide-y divide-ink-100">
              {breakdown.data.qrByCode.slice(0, 8).map((code) => (
                <li key={code.code} className="flex items-center justify-between gap-3 py-2.5">
                  <span className="flex flex-col">
                    <span className="text-body-s text-ink-950">{code.label}</span>
                    <span className="font-mono text-caption text-ink-400">{code.code}</span>
                  </span>
                  <span className="flex items-center gap-3">
                    {code.scans === 0 ? <Badge tone="warning">fără scanări</Badge> : null}
                    <span className="nums text-body-s font-medium text-ink-950">{formatNumber(code.scans)}</span>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title="Niciun cod QR încă" description={'Generează primul cod din secțiunea „Coduri QR” pentru a urmări scanările.'} />
          )}
        </Card>

        <Card>
          <CardHeader title="Ce caută clienții" subtitle="Termenii introduși în căutarea din meniu" />
          {breakdown.loading ? (
            <Skeleton className="h-32" />
          ) : breakdown.data?.searches.length ? (
            <ul className="flex flex-wrap gap-2">
              {breakdown.data.searches.slice(0, 12).map((search) => (
                <li
                  key={search.term}
                  className="flex items-center gap-2 rounded-full border border-ink-100 bg-surface-2 px-3 py-1.5 text-body-s text-ink-800"
                >
                  {search.term}
                  <span className="nums text-caption text-ink-400">{search.count}</span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              title="Încă nicio căutare"
              description="Când un client folosește căutarea din meniu, termenul apare aici — fără identificarea persoanei."
            />
          )}
        </Card>
      </div>

      <p className="mt-6 text-caption text-ink-400">
        Ultima actualizare a agregatelor:{' '}
        {overview.data ? `${formatDateTime(new Date().toISOString())} (agregatele se recalculează la fiecare 10 minute)` : '—'}
      </p>
    </div>
  );
}
